import { formatDateTime } from "@eusoft/webapp-framework/helpers/Format";
import { forModel } from "@eusoft/webapp-jsx";
import { Content, contentInfo, formatCurrency, formatEnum, formatText,  type IAction } from "@eusoft/webapp-ui";
import { refundOrderAsync } from "../../actions/Order";
import { Card } from "../../components/Card";
import { Field } from "../../components/Field";
import { StatusTag } from "../../components/StatusTag";
import { OrderItemType, OrderStatus, OrderType, RefundOrderReason, type Guid, type IOrderDetailsView } from "../../entities/Commands";
import { formatOrderType, formatOrderStatus, formatPaymentMethod } from "../../helpers/Format";
import { Authorize } from "../../services/Authorize";
import { executeAsync } from "../../services/DataService";
import { userSession } from "../../services/UserSession";
import "./OrderDetailsPage.scss";
import { navigatePage } from "../../actions";
import { registerComponent } from "@eusoft/webapp-core/Component";

export interface IOrderDetailsArgs {
    id?: Guid;
    order?: IOrderDetailsView;  
}


export class OrderDetailsPage extends Content<IOrderDetailsArgs> {

    constructor() {

        super();

        const actions: IAction[] = [];


        this.init(OrderDetailsPage, {
            title: "order",
            style: ["panel"],            
            body: forModel(this, m => <>
                <Card header="general" style="fieldset">
                    <Field label="date">{formatDateTime(m.order.date)}</Field>
                    <Field label="type">{formatOrderType(m.order.type)}</Field>
                    <Field label="sale-point">{m.order.salePoint.name}</Field>
                    <Field label="merchant">{m.order.merchant?.name}</Field>
                    <Field label="customer">{m.order.customer?.name}</Field>
                    <Field label="card-state">
                        <StatusTag {...formatOrderStatus(m.order.status)} />
                    </Field> 
                </Card>
                <Card header="items" name="items" style="fieldset">
                    {m.order.items.forEach(a => <>
                        <div>{a.type != OrderItemType.Percentage ? `${a.quantity} x`: ""}</div>
                        <div>{a.type != OrderItemType.Percentage ? (a.description ?? formatCurrency(a.unitAmount)) : a.description + " %"}</div>
                        <div>{formatCurrency(a.unitAmount * a.quantity)}</div>
                    </>)}
                    <div></div>
                    <div className="footer">{formatText("total")}</div>
                    <div className="footer">{formatCurrency(m.order.total)}</div>
                </Card>
                {m.order.payment && <Card header="payment" name="payment" style="fieldset">
                    <Field label="date">{formatDateTime(m.order.payment.time)}</Field>
                    <Field label="method">{formatPaymentMethod(m.order.payment.method)}</Field>
                    <Field openAsync={()=> m.showCardInfo(m.order.payment.cardCode)}
                        canOpen={m.order.payment.cardCode && !userSession.active.walletId}
                        label="card-code">{m.order.payment.cardCode}</Field>
                </Card>}
                {m.order.refund && <Card header="refund" name="refund" style="fieldset">
                    <Field label="date">{formatDateTime(m.order.refund.time)}</Field>
                    <Field label="amount">{formatCurrency(m.order.refund.amount)}</Field>
                    <Field label="reason">{formatEnum(RefundOrderReason, m.order.refund.reason)}</Field>
                    <Field label="note">{m.order.refund.note}</Field>
                </Card>}
            </>)            
        });
    }

    showCardInfo(cardCode: string) {
        navigatePage("card-details", { cardCode });
    }

    async refreshAsync() {
        this.order = await executeAsync("GetOrder", { id: this.order.id });
        this.updateActions();
    }


    updateActions() {
        this.actions = [];

        if (userSession.hasPermission("Order", "write") &&
            userSession.isBusiness &&
            this.order.status == OrderStatus.Paid &&
            this.order.type == OrderType.Buy) {

            this.actions.push({
                name: "refund",
                text: "refund-action",
                executeAsync: async () => {

                    if (await refundOrderAsync(this.order))
                        await this.refreshAsync();
                }
            });
        }
    }


    override async onLoadAsync(args: IOrderDetailsArgs) {

        if (!args.order)
            args.order = await executeAsync("GetOrder", { id: args.id });

        this.order = args.order;

        this.updateActions();
      
        return true;
    }

    order: IOrderDetailsView;

    static override info = contentInfo({
        name: "order-details",
        route: "/orders/{id}",
        factory: () => new OrderDetailsPage(),
        features: [
            Authorize<IOrderDetailsArgs>(a => a
                .allowAnonymous()
                .allowTemporary()
            ),
        ]
    });
}

registerComponent(OrderDetailsPage, "OrderDetailsPage");